import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, Dimensions, Text, Platform } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import { RootState } from "../../../store";
import { selectIsLogged } from "../../../store/auth/authReducer";
import { env } from "../../../utils/env";
import { setActivePlayers } from "../../../store/invites/inviteSlice"; // Import the action
import { getActivePlayers } from "../../../services/api";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import { getSocket } from "../../../services/socket";
// Extend Window interface for PIXI game functions
declare global {
  interface Window {
    startGame: (data: any) => void;
    destroyGame: () => void;
    startRedBlackGame: (data: any) => void;
    startScratchGame: (data: any) => void;
    startSlotGame: (data: any) => void;
    socket: any;
  }
}
let globalJustSwitchedTable = false;
type NavigationProp = NativeStackNavigationProp<RootStackParamList, "Game">;
interface GameTableProps {
  tableId?: number | string;
  legacyRoomId?: number;
  roomSlug?: string;
  isTutorial?: boolean;
  onGameEvent?: (event: string, data?: any) => void;
  isTournamentRoom?: boolean;
}

const GameTable: React.FC<GameTableProps> = ({
  tableId,
  legacyRoomId = 1,
  roomSlug,
  isTutorial = false,
  onGameEvent,
  isTournamentRoom = false,
}) => {
  const dispatch = useDispatch();
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameData, setGameData] = useState<any>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  console.log("🌐 GameTable FILE = web, Platform =", Platform.OS);

  // Get auth data from Redux store
  const auth = useSelector((state: RootState) => state.auth);
  const i18nBundle = useSelector((state: RootState) => state.i18n.bundle);
  const langCode = useSelector((state: RootState) => state.i18n.langCode);

  const isLogged = useSelector(selectIsLogged);
  const socket = useSelector((state: RootState) => state.socket?.socket);
  const navigation = useNavigation<NavigationProp>();

  useEffect(() => {
    if (!isLogged || !auth.user?.id || !auth.user?.username) {
      console.log("QuickSeat: Waiting for auth...");
      return;
    }
    const socket = getSocket(String(auth.user.id), String(auth.user.username));

    if (!socket) {
      console.log("No socket available for quickSeat listener");
      return;
    }

    const handleQuickSeat = (packet: any) => {
      console.log("Packet:", packet);
      console.log("Current tableId:", tableId);

      const data = packet?.data || packet || {};

      if (packet?.success && data.tableId && data.tableId !== tableId) {
        console.log(`SWITCHING from table ${tableId} to ${data.tableId}`);
        globalJustSwitchedTable = true;
        if (window.destroyGame && gameStarted) {
          try {
            window.destroyGame();
            setGameStarted(false);
          } catch (error) {
            console.error("Failed to destroy game:", error);
          }
        }

        navigation.replace("Game", {
          tableId: data.tableId,
          roomSlug: data.roomSlug || roomSlug,
          isTournamentRoom: false,
        });
        setTimeout(() => {
          globalJustSwitchedTable = false;
        }, 2000);
      } else if (packet?.success && data.tableId === tableId) {
        console.log("ℹQuickSeat to same table, no navigation needed");
      }
    };

    socket.on("quickSeat", handleQuickSeat);
    console.log("QuickSeat listener registered successfully");

    return () => {
      socket.off("quickSeat", handleQuickSeat);
    };
  }, [isLogged, auth.user, tableId, gameStarted, navigation]);

  useEffect(() => {
    const handleHashChange = () => {
      if (globalJustSwitchedTable) {
        console.log("Ignoring hash change - just switched tables");
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }

      const hash = window.location.hash;
      console.log("Hash changed tooo:", hash);

      const roomMatch = hash.match(/#\/room\/([^/]+)/);
      const lobbyMatch = hash === "#/lobby" || hash.startsWith("#/lobby");
      if (roomMatch && onGameEvent) {
        const roomSlug = roomMatch[1];
        console.log("Detected room navigation to:", roomSlug);

        window.history.replaceState(null, "", window.location.pathname);
        onGameEvent("goToLobby", { roomSlug, roomId: roomSlug });
      } else if (lobbyMatch && onGameEvent) {
        window.history.replaceState(null, "", window.location.pathname);
        onGameEvent("goToLobby");
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [onGameEvent]);

  // Check authentication and redirect to login if not logged in
  useEffect(() => {
    if (!isLogged) {
      console.log("🔐 User not logged in, redirecting to login...");
      navigation.navigate("Login" as never);
    }
  }, [isLogged, navigation]);

  // Load PIXI game scripts dynamically
  useEffect(() => {
    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
      });

    const waitForPixi = () =>
      new Promise<void>((resolve, reject) => {
        const started = Date.now();
        const interval = setInterval(() => {
          if (window.PIXI) {
            clearInterval(interval);
            resolve();
          } else if (Date.now() - started > 5000) {
            clearInterval(interval);
            reject(new Error("PIXI not available after loading game.js"));
          }
        }, 50);
      });

    const loadGameScripts = async () => {
      if (scriptsLoaded) return;

      try {
        await loadScript("/svaragame/scripts/NoSleep.min.js");
        await loadScript("/svaragame/scripts/screenfull.js");
        await loadScript("/svaragame/scripts/game.js");

        // Ensure PIXI is available before attaching particles extension
        await waitForPixi();
        await loadScript("/svaragame/scripts/pixi-particles.min.js");
        console.log("🎮 PIXI particles script loaded");

        setScriptsLoaded(true);
        console.log("🎮 PIXI game scripts loaded successfully");

        // Check if startGame function is available
        if (window.startGame) {
          console.log("✅ window.startGame is available");
        } else {
          console.error(
            "❌ window.startGame is not available after script loading",
          );
        }
      } catch (error) {
        console.error("❌ Failed to load game scripts:", error);
      }
    };

    loadGameScripts();
  }, [scriptsLoaded]);

  // Initialize game data
  useEffect(() => {
    console.log("🎮 GameTable auth effect triggered:", { auth, isLogged });

    if (!isLogged || !auth.token) {
      console.log("⏳ Waiting for authentication...");
      return;
    }

    // Get proper game dimensions like the Angular app
    const getGameDimensions = () => {
      let ratio = 1;
      const w = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0,
      );
      const h = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0,
      );

      if (
        // @ts-ignore
        window.screen.systemXDPI !== undefined &&
        // @ts-ignore
        window.screen.logicalXDPI !== undefined &&
        // @ts-ignore
        window.screen.systemXDPI > window.screen.logicalXDPI
      ) {
        // @ts-ignore
        ratio = window.screen.systemXDPI / window.screen.logicalXDPI;
      } else if (window.devicePixelRatio !== undefined) {
        ratio = window.devicePixelRatio;
      }

      console.log("🎮 Game dimensions:", { width: w, height: h, ratio });
      return { width: w, height: h, ratio };
    };

    const dimensions = getGameDimensions();

    const gameInitData = {
      userId: auth.user?.numericId,
      tableId,
      width: dimensions.width * dimensions.ratio,
      height: dimensions.height * dimensions.ratio,
      ownerId: undefined,
      tutorial: isTutorial,
      isVipRoom: legacyRoomId === 5,
      isTournamentRoom: isTournamentRoom,
      tableLevel: legacyRoomId,
      playerName: auth.user?.username,
      // playerAvatarUrl: auth.user?.avatarFileId,
      // ||
      // "https://play.vipsvara.com/assets/avatar_man.png?v=63",
      // "https://play.vipsvara.com/assets/loading_screen_en.jpg?v=63",
      soundEnabled:
        auth.user?.isSoundEffectsEnabled !== undefined
          ? auth.user.isSoundEffectsEnabled
          : true,
      translations: i18nBundle || {},
      language: langCode || "en",
      drawingBuffer: false,
      payToPlay: (auth.user?.level || 1) >= 10,
      freeToPlay: false,
      payToPlayMinLevel: 10,
      token: auth.token, // Use the actual token from auth
      api: {
        game:
          `${env("SVARA_API_URL")}/api/game` ||
          "https://play.vipsvara.com/api/game",
        user:
          `${env("SVARA_API_URL")}/api/user` ||
          "https://play.vipsvara.com/api/user",
      },
      eventRunning: "",
      isEventRunning: false,
      powerMeter: !!auth.user?.isHandPowerEnabled,
    };

    console.log("🎮 Auth object:", auth);
    console.log("🎮 Auth token:", auth.token);
    console.log("🎮 Auth user:", auth.user);
    console.log("🎮 Game init data:", gameInitData);
    setGameData(gameInitData);
  }, [auth, isLogged, tableId, isTutorial]);

  // Handle game events from PIXI
  useEffect(() => {
    const handleGameEvents = (event: CustomEvent) => {
      console.log("eventtttt", event);
      console.log("Game event:", event.type, event.detail);
      console.log("event invite", event.type);
      switch (event.type) {
        case "becomeVIP":
          onGameEvent?.("becomeVIP");
          break;
        case "notEnoughChips":
          onGameEvent?.("notEnoughChips");
          break;
        case "buyChips":
          onGameEvent?.("buyChips");
          break;
        case "invite":
          const fetchAndShowInviteModal = async () => {
            try {
              const activePlayers = await getActivePlayers({
                start: 0,
                limit: 100,
                tableId: tableId,
              });
              console.log(
                "🔍 Fetched active players count:",
                activePlayers.data.items.length,
              );
              console.log("Active players fetched:", activePlayers);
              dispatch(setActivePlayers(activePlayers.data.items));
              onGameEvent?.("invite", activePlayers);
            } catch (error) {
              console.error("Failed to fetch active players:", error);
              onGameEvent?.("invite", []);
            }
          };
          fetchAndShowInviteModal();
          break;

        case "setSound":
          onGameEvent?.("setSound", event.detail);
          break;
        case "openNotifications":
          onGameEvent?.("openNotifications");
          break;
        case "showBlackscreenAlert":
          onGameEvent?.("showBlackscreenAlert");
          break;
        case "hideBlackscreenAlert":
          onGameEvent?.("hideBlackscreenAlert");
          break;
        case "tableDoesNotExist":
          onGameEvent?.("tableDoesNotExist");
          break;
      }
    };

    const handleInviteReceived = (data: any) => {
      console.log("Received invite:", data);
      onGameEvent?.("inviteReceived", data);
    };

    // Add event listeners
    const events = [
      "becomeVIP",
      "notEnoughChips",
      "buyChips",
      "invite",
      "setSound",
      "openNotifications",
      "showBlackscreenAlert",
      "hideBlackscreenAlert",
      "tableDoesNotExist",
    ];

    events.forEach((eventType) => {
      window.addEventListener(eventType, handleGameEvents);
    });

    if (socket) {
      socket.on("inviteUser", handleInviteReceived);
    }

    return () => {
      events.forEach((eventType) => {
        window.removeEventListener(eventType, handleGameEvents);
      });
    };
  }, [onGameEvent]);

  // Initialize game when data is ready and scripts are loaded
  useEffect(() => {
    console.log("🎮 GameTable useEffect triggered:", {
      gameData: !!gameData,
      scriptsLoaded,
      startGameAvailable: !!window.startGame,
      gameStarted,
    });

    if (!gameData) {
      console.log("⏳ Waiting for game data...");
      return;
    }

    if (!scriptsLoaded) {
      console.log("⏳ Waiting for scripts to load...");
      return;
    }

    if (gameStarted) {
      console.log("⏳ Game already started");
      return;
    }

    // Function to attempt starting the game
    const attemptStartGame = () => {
      if (!window.startGame) {
        console.error("❌ window.startGame is not available!");
        return false;
      }

      try {
        // Make socket available to PIXI game
        if (socket) {
          window.socket = socket;
          console.log("🔌 Socket made available to PIXI game");
        }

        // Ensure the content div exists and is ready
        const contentDiv = document.getElementById("content");
        if (!contentDiv) {
          console.error("❌ Content div not found!");
          return false;
        }

        console.log("🎮 Starting PIXI game with data:", gameData);
        console.log("🎮 Content div found:", contentDiv);

        // Start the game
        window.startGame(gameData);
        setGameStarted(true);
        console.log("🎮 PIXI game started successfully");

        // Check if canvas was created
        setTimeout(() => {
          const canvas = contentDiv.querySelector("canvas");
          if (canvas) {
            console.log("✅ PIXI canvas found:", canvas);
            console.log("Canvas dimensions:", canvas.width, "x", canvas.height);
            console.log(
              "Canvas style dimensions:",
              canvas.style.width,
              "x",
              canvas.style.height,
            );
            console.log(
              "Content div dimensions:",
              contentDiv.offsetWidth,
              "x",
              contentDiv.offsetHeight,
            );
            console.log("Content div style:", contentDiv.style.cssText);
          } else {
            console.error("❌ No PIXI canvas found in content div");
            console.log("Content div children:", contentDiv.children);
          }
        }, 1000);

        return true;
      } catch (error) {
        console.error("Failed to start game:", error);
        return false;
      }
    };

    // Try to start the game immediately
    if (attemptStartGame()) {
      return;
    }

    // If startGame is not available, retry with increasing delays
    const retryDelays = [100, 200, 500, 1000];
    let retryCount = 0;

    const retryStartGame = () => {
      if (retryCount >= retryDelays.length) {
        console.error("❌ Failed to start game after all retries");
        return;
      }

      const delay = retryDelays[retryCount];
      console.log(
        `⏳ Retrying game start in ${delay}ms (attempt ${retryCount + 1})`,
      );

      setTimeout(() => {
        if (attemptStartGame()) {
          return;
        }
        retryCount++;
        retryStartGame();
      }, delay);
    };

    retryStartGame();
  }, [gameData, scriptsLoaded, socket, gameStarted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameStarted && window.destroyGame) {
        try {
          window.destroyGame();
        } catch (error) {
          console.error("Failed to destroy game:", error);
        }
      }
    };
  }, [gameStarted]);

  return (
    <View style={styles.container}>
      {!scriptsLoaded && (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading game...</Text>
        </View>
      )}
      <View
        nativeID="content"
        ref={gameContainerRef as any}
        style={styles.gameContainer}
      />
      {/* <View id="content" ref={gameContainerRef} style={styles.gameContainer} /> */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
    height: "100vh", // Ensure container has full viewport height
    width: "100vw", // Ensure container has full viewport width
  },
  gameContainer: {
    width: "100vw", // Use viewport width
    height: "100vh", // Use viewport height
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#420304", // Same as PIXI background
    display: "flex", // Ensure proper display
    alignItems: "center", // Center the canvas
    justifyContent: "center", // Center the canvas
  },
  loadingContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#000",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  loadingText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "bold",
  },
});

export default GameTable;
